import { useState, useCallback, useEffect } from 'react';
import { walletApi } from '@/services/api';
import { useRealtime } from '@/providers/realtime-provider';
import type { WalletBalance, WalletTransaction, Withdrawal, WithdrawalRequest, PayoutMethod } from '@/types';

export function useWallet() {
  const [balance, setBalance] = useState<WalletBalance | null>(null);
  const [transactions, setTransactions] = useState<WalletTransaction[]>([]);
  const [withdrawals, setWithdrawals] = useState<Withdrawal[]>([]);
  const [payoutMethods, setPayoutMethods] = useState<PayoutMethod[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const { events } = useRealtime();

  const fetchBalance = useCallback(async () => {
    try {
      const response = await walletApi.getBalance();
      if (response?.success && response.data) {
        const balances = Array.isArray(response.data) ? response.data : [response.data];
        const buckets = {
          pending: 0, approved: 0, rejected: 0, revenue: 0,
          bonus: 0, referral: 0, frozen: 0, withdrawable: 0
        };
        let totalBalance = 0;
        balances.forEach((b: any) => {
          if (b.bucket && typeof b.amount_usd === 'number') {
            (buckets as Record<string, number>)[b.bucket] = b.amount_usd;
            totalBalance += b.amount_usd;
          }
        });
        (buckets as Record<string, number>).withdrawable = buckets.approved;
        setBalance({
          userId: '',
          buckets,
          totalBalance,
          currency: 'USD',
          updatedAt: new Date().toISOString(),
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch balance'));
    }
  }, []);

  const fetchTransactions = useCallback(async (params?: { page?: number; limit?: number; type?: string }) => {
    try {
      const response = await walletApi.getTransactions(params);
      if (response?.success && response.data) {
        const items = Array.isArray(response.data) ? response.data : [response.data];
        setTransactions(items.map((t: any) => ({
          id: t.id,
          userId: t.user_id || t.userId,
          type: t.type,
          bucket: t.bucket,
          amount: t.amount_usd,
          currency: t.currency,
          status: t.status,
          referenceId: t.source_id,
          referenceType: t.source_type,
          description: t.meta,
          metadata: {},
          createdAt: new Date(t.created_at || t.createdAt).toISOString(),
          updatedAt: new Date(t.updated_at || t.createdAt).toISOString(),
        })));
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch transactions'));
    }
  }, []);

  const fetchWithdrawals = useCallback(async (params?: { page?: number; limit?: number; status?: string }) => {
    try {
      const response = await walletApi.getWithdrawals(params);
      if (response?.success && response.data) {
        const items = Array.isArray(response.data) ? response.data : [response.data];
        setWithdrawals(items.map((w: any) => ({
          id: w.id,
          userId: w.user_id || w.userId,
          payoutMethodId: w.payout_method_id,
          amount: w.amount_usd,
          currency: w.currency,
          status: w.status,
          feeAmount: w.fee_usd || 0,
          netAmount: w.net_amount_usd || w.amount_usd,
          processedAt: w.processed_at ? new Date(w.processed_at).toISOString() : null,
          completedAt: w.completed_at ? new Date(w.completed_at).toISOString() : null,
          failureReason: w.rejection_reason || w.failure_reason || null,
          metadata: {},
          createdAt: new Date(w.created_at || w.createdAt).toISOString(),
          updatedAt: new Date(w.updated_at || w.createdAt).toISOString(),
        })));
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch withdrawals'));
    }
  }, []);

  const fetchPayoutMethods = useCallback(async () => {
    try {
      const response = await walletApi.getPayoutMethods();
      if (response?.success && response.data) {
        const items = Array.isArray(response.data) ? response.data : [response.data];
        setPayoutMethods(items.map((pm: any) => ({
          id: pm.id,
          userId: pm.user_id || '',
          type: pm.type || pm.code || 'other',
          name: pm.name || pm.label || pm.code,
          details: typeof pm.details === 'string' ? JSON.parse(pm.details) : (pm.details || {}),
          isDefault: !!pm.is_default,
          verified: !!pm.verified,
          verifiedAt: pm.verified_at ? new Date(pm.verified_at).toISOString() : null,
          createdAt: pm.created_at ? new Date(pm.created_at).toISOString() : new Date().toISOString(),
          updatedAt: pm.updated_at ? new Date(pm.updated_at).toISOString() : new Date().toISOString(),
        })));
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch payout methods'));
    }
  }, []);

  const requestWithdrawal = useCallback(async (data: WithdrawalRequest) => {
    setLoading(true);
    setError(null);
    try {
      const response = await walletApi.requestWithdrawal(data);
      if (response?.success && response.data) {
        const w = response.data;
        const withdrawal = {
          id: w.id,
          userId: w.user_id || '',
          payoutMethodId: w.payout_method_id,
          amount: w.amount_usd,
          currency: w.currency,
          status: w.status,
          feeAmount: w.fee_usd || 0,
          netAmount: w.net_amount_usd || w.amount_usd,
          processedAt: w.processed_at ? new Date(w.processed_at).toISOString() : null,
          completedAt: w.completed_at ? new Date(w.completed_at).toISOString() : null,
          failureReason: w.rejection_reason || null,
          metadata: {},
          createdAt: new Date(w.created_at || Date.now()).toISOString(),
          updatedAt: new Date(w.updated_at || w.created_at || Date.now()).toISOString(),
        };
        setWithdrawals(prev => [withdrawal, ...prev]);
        await fetchBalance();
        return withdrawal;
      }
      throw new Error('Withdrawal request failed');
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Withdrawal request failed'));
      return null;
    } finally {
      setLoading(false);
    }
  }, [fetchBalance]);

  const addPayoutMethod = useCallback(async (data: { type: string; details: Record<string, string>; label?: string }) => {
    setLoading(true);
    setError(null);
    try {
      const response = await walletApi.addPayoutMethod(data);
      if (response?.success && response.data) {
        const pm = response.data;
        const payoutMethod = {
          id: pm.id,
          userId: pm.user_id || '',
          type: pm.type || 'other',
          name: pm.name || pm.label || pm.code,
          details: typeof pm.details === 'string' ? JSON.parse(pm.details) : (pm.details || {}),
          isDefault: !!pm.is_default,
          verified: !!pm.verified,
          verifiedAt: pm.verified_at ? new Date(pm.verified_at).toISOString() : null,
          createdAt: new Date(pm.created_at || Date.now()).toISOString(),
          updatedAt: new Date(pm.updated_at || pm.created_at || Date.now()).toISOString(),
        };
        setPayoutMethods(prev => [...prev, payoutMethod]);
        return payoutMethod;
      }
      throw new Error('Failed to add payout method');
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to add payout method'));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const deletePayoutMethod = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      await walletApi.deletePayoutMethod(id);
      setPayoutMethods(prev => prev.filter(m => m.id !== id));
      return true;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to delete payout method'));
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const setDefaultPayoutMethod = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      await walletApi.setDefaultPayoutMethod(id);
      setPayoutMethods(prev => prev.map(m => ({ ...m, isDefault: m.id === id })));
      return true;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to set default payout method'));
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  // Handle realtime events
  useEffect(() => {
    events.forEach(event => {
      if (event.type === 'wallet_updated' && event.payload) {
        setBalance(prev => prev ? { ...prev, ...event.payload } : event.payload as unknown as WalletBalance);
      }
      if (event.type === 'withdrawal_status_changed' && event.payload) {
        setWithdrawals(prev => prev.map(w => w.id === event.payload?.id ? { ...w, ...event.payload } : w));
      }
    });
  }, [events]);

  return {
    balance,
    transactions,
    withdrawals,
    payoutMethods,
    loading,
    error,
    fetchBalance,
    fetchTransactions,
    fetchWithdrawals,
    fetchPayoutMethods,
    requestWithdrawal,
    addPayoutMethod,
    deletePayoutMethod,
    setDefaultPayoutMethod,
    refetch: () => {
      fetchBalance();
      fetchTransactions();
      fetchWithdrawals();
      fetchPayoutMethods();
    },
  };
}
